import { FileText, Download, Plus, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../services/user.service';

interface PersonalizedHeaderProps {
  onAddDevice: () => void;
  onProfile?: () => void;
}

export function PersonalizedHeader({ onAddDevice, onProfile }: PersonalizedHeaderProps) {
  const { user } = useAuth();

  const { data: userData, isLoading } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: () => userService.getProfile(),
    enabled: !!user,
  });

  const profile = userData?.profile || userData || {};

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getFullName = () => {
    const displayName = profile.displayName || profile.display_name;
    if (displayName) return displayName;
    return user?.email?.split('@')[0] || 'User';
  };

  const handleCreatePDF = () => {
    alert('PDF Report generation will be implemented');
  };

  const handleExportCSV = () => {
    alert('CSV Export will be implemented');
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm mb-8 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-sm mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold text-gray-900">{getFullName()}</h2>
            {onProfile && (
              <button
                onClick={onProfile}
                className="text-dropsugar-green hover:text-dropsugar-green-dark text-sm font-medium flex items-center gap-1"
              >
                <User className="w-4 h-4" />
                Profile
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-gray-600">DOB: </span>
              <span className="font-medium text-gray-900">
                {formatDate(profile.dateOfBirth || profile.date_of_birth)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">MRN: </span>
              <span className="font-medium text-gray-900">{profile.mrn || '—'}</span>
            </div>
            <div>
              <span className="text-gray-600">Diabetes: </span>
              <span className="font-medium text-gray-900">
                {profile.diabetesType || profile.diabetes_type || '—'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleCreatePDF}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium text-gray-700"
          >
            <FileText className="w-4 h-4" />
            Create PDF Report
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium text-gray-700"
          >
            <Download className="w-4 h-4" />
            Export to CSV
          </button>
          <button
            onClick={onAddDevice}
            className="flex items-center gap-2 px-4 py-2.5 bg-dropsugar-green text-white rounded-lg hover:bg-dropsugar-green-dark transition-all font-medium shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add device
          </button>
        </div>
      </div>
    </div>
  );
}
